import React, { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { colors, TableBody, TextArea, SearchActionBar } from '@insurancemenu/imenu-components-lib';
import { ErrorOutline } from '@insurancemenu/imenu-components-lib/dist/icons';
import ActionModal from 'components/common/modals/ActionModal';
import { useInitialSeedProductTemplatesStore } from 'store/pages/rfq/rfq-template/initial-seed-product-templates/pageStore';
import { ProductTemplateSortBy, ProductTemplateSortOrder } from 'types/productServiceV2';
import TableRow from './TableRow';
import {
  ContentStyled,
  HeadingsRowStyled,
  TableHeaderStyled,
  TableHeadingStyled,
  TableWrapperStyled,
  TemplateTableStyled,
} from './styles';

const InitialSeedProductTemplatesTable: React.FC = observer(() => {
  const {
    isLoading,
    paginationDelegate: {
      page,
      sortBy,
      sortOrder,
      filters,
      totalPages,
      totalElements,
      setPage,
      setTotalPages,
      setFilterValue,
      setSortBy,
      setSortOrder,
    },
    errorMessageModalDelegate,
    productTemplates,
    getInitialData,
  } = useInitialSeedProductTemplatesStore();

  useEffect(() => {
    void getInitialData();
  }, [page]);

  useEffect(() => {
    setPage(1);
    setTotalPages(1);
    void getInitialData();
  }, [filters.productName, filters.doesNotHaveBenefitsFile, filters.hasValidationErrors, sortBy, sortOrder]);

  const onSearch = (value: string): void => {
    setFilterValue('productName', value);
    setPage(1);
  };

  const onReset = (): void => {
    setFilterValue('productName', '');
    setPage(1);
  };

  const onTableSort = (sortKey: string, sortingOrder: 'ASC' | 'DESC'): void => {
    setSortBy(sortKey as ProductTemplateSortBy);
    setSortOrder(sortingOrder as ProductTemplateSortOrder);
  };

  return (
    <>
      <ContentStyled>
        <TableWrapperStyled>
          <SearchActionBar
            totalElements={totalElements}
            totalTitle="Total Products"
            searchPlaceholder="Internal Product Name"
            isSearchButtonDisabled={isLoading}
            onSearch={onSearch}
            onReset={onReset}
          />
          <TemplateTableStyled
            loading={isLoading}
            maxHeight="55rem"
            minHeight="55rem"
            showScrollShadows
            showRowHover
            totalElements={totalElements}
            pagination={{
              onPageChange: setPage,
              maxPage: totalPages || 1,
              tablePage: page,
            }}
          >
            <TableHeaderStyled>
              <HeadingsRowStyled>
                <TableHeadingStyled
                  width="8rem"
                  sorting
                  sortingKey={ProductTemplateSortBy.NAME}
                  onSortCallback={onTableSort}
                >
                  Internal Product Name
                </TableHeadingStyled>
                <TableHeadingStyled width="5rem">External Product Name</TableHeadingStyled>
                <TableHeadingStyled width="5rem">Client Number</TableHeadingStyled>
                <TableHeadingStyled width="6rem">Benefit Summary PDF</TableHeadingStyled>
                <TableHeadingStyled
                  width="6rem"
                  sorting
                  sortingKey={ProductTemplateSortBy.DATE}
                  onSortCallback={onTableSort}
                >
                  Creation Date
                </TableHeadingStyled>
                <TableHeadingStyled width="3rem">SYNC</TableHeadingStyled>
                <TableHeadingStyled width="4rem" />
              </HeadingsRowStyled>
            </TableHeaderStyled>
            <TableBody>
              {productTemplates.map(
                ({ groupId, clientGroupId, benefitSummaryLink, jsonLink, errorMessage, template }) => (
                  <TableRow
                    key={template.id}
                    productTemplateId={template.id}
                    internalName={template.internalName}
                    externalName={template.name}
                    jsonLink={jsonLink}
                    clientGroupId={clientGroupId}
                    creationDateTime={template.creationDateTime}
                    benefitSummaryPdfLink={benefitSummaryLink}
                    errorMessage={errorMessage}
                    internalGroupId={groupId}
                  />
                ),
              )}
            </TableBody>
          </TemplateTableStyled>
        </TableWrapperStyled>
      </ContentStyled>
      <ActionModal
        show={errorMessageModalDelegate.isVisible}
        icon={<ErrorOutline width="5.6rem" color={colors.red[600]} />}
        title="Error Cause"
        modalContent={
          <div>
            <TextArea
              disabled
              name="errorCause"
              value={errorMessageModalDelegate.errorMessage}
              height="20rem"
              width="100%"
            />
          </div>
        }
        onClose={errorMessageModalDelegate.close}
      />
    </>
  );
});

export default InitialSeedProductTemplatesTable;
